import {useParentCompany, useLinkedCompanies} from '@api'
import {FormFieldType, NavigationPathKey, TranslationKey} from '@enums'
import {GridColDef} from '@mui/x-data-grid'
import {pageLinksAtom} from '@state'

import {useEffect, useMemo, useState} from 'react'

import {activeCell, countryCell, FormFieldItem, isCompanyUser, renderClickableCell, useViewNavigate} from 'common'
import {useSetAtom} from 'jotai'
import {useTranslation} from 'react-i18next'
import {useParams} from 'react-router-dom'

import {ComposedComponents} from '@components'

export enum ParentCompanyTab {
  GENERAL,
  COMPANIES,
}

export interface CompanyTableData {
  name: string
  description: string
  country: string
  active: boolean
}

export function useParentCompanyViewModel() {
  const setTitleLinks = useSetAtom(pageLinksAtom)
  const [activeTab, setActiveTab] = useState<ParentCompanyTab>(ParentCompanyTab.GENERAL)
  const navigate = useViewNavigate()
  const {t} = useTranslation()
  const {id} = useParams()

  const {data: parentCompanyData} = useParentCompany(id)
  const {data: linkedCompaniesData} = useLinkedCompanies(id)

  const companyRows = useMemo(() => {
    if (!linkedCompaniesData) return []
    return linkedCompaniesData.map(item => ({
      name: item.name,
      description: item.description,
      country: item.country || '',
      active: item.active,
      id: item.id,
    }))
  }, [linkedCompaniesData])

  useEffect(
    () =>
      setTitleLinks([
        {title: t(TranslationKey['Admin panel']), link: NavigationPathKey.ADMIN_PANEL},
        {title: t(TranslationKey.Resources), link: NavigationPathKey.RESOURCES_DASHBOARD},
        {
          title: t(TranslationKey['Amplio partners']),
          link: NavigationPathKey.PARENT_COMPANY_SEARCH,
        },
        {title: t(TranslationKey['Partner view'])},
      ]),
    [],
  )

  /**
   * Edit button should be available only for Administrator Users
   */
  const isEditButtonVisible = !isCompanyUser()

  /**
   * Tab buttons configuration
   */
  const tabButtons: ComposedComponents.TabButtonProps<ParentCompanyTab>[] = [
    {
      label: t(TranslationKey['General information']),
      key: ParentCompanyTab.GENERAL,
      onClick: () => setActiveTab(ParentCompanyTab.GENERAL),
    },
    {
      label: t(TranslationKey.Companies),
      key: ParentCompanyTab.COMPANIES,
      onClick: () => setActiveTab(ParentCompanyTab.COMPANIES),
    },
  ]

  /**
   * Parent Company form fields configuration - general section
   */
  const parentCompanyGeneralFormFields: FormFieldItem[] = [
    {
      fieldType: FormFieldType.LABEL,
      label: t(TranslationKey['Partner name']),
      text: parentCompanyData?.name || '-',
    },
    {
      fieldType: FormFieldType.LABEL,
      label: t(TranslationKey.Description),
      text: parentCompanyData?.description || '-',
    },
    {
      fieldType: FormFieldType.LABEL,
      label: t(TranslationKey.Notes),
      text: parentCompanyData?.note || '-',
    },
    {
      fieldType: FormFieldType.LABEL,
      label: t(TranslationKey.Active),
      text: parentCompanyData?.active ? t(TranslationKey.Active) : t(TranslationKey.Inactive),
    },
  ]

  /**
   * Company table columns definition
   */
  const companyColumns: GridColDef[] = [
    {
      field: 'name',
      headerName: t(TranslationKey.Name),
      flex: 0.8,
      hideable: false,
      renderCell: params =>
        renderClickableCell(params, () => navigate(`${NavigationPathKey.COMPANY_VIEW}/${params.row.id}`)),
    },
    {field: 'description', headerName: t(TranslationKey.Description), flex: 1},
    countryCell(t),
    activeCell(t),
  ]

  /**
   * A function handling the Back button click
   */
  const onPressBack = () => {
    navigate(NavigationPathKey.PARENT_COMPANY_SEARCH)
  }

  /**
   * A function handling Edit button click
   */
  const onPressEdit = (): void => {
    navigate(`${NavigationPathKey.PARENT_COMPANY_CREATION}/${id}`)
  }

  /**
   * Create button should only be visible for Administrator users
   */
  const showCreateButton = !isCompanyUser()

  return {
    parentCompanyGeneralFormFields,

    t,
    showCreateButton,
    isEditButtonVisible,
    tabButtons,
    activeTab,
    companyColumns,
    companyRows,
    onPressBack,
    onPressEdit,
    navigate,
  }
}
